/**
 * Product sync row (Phase 65 / BLU-594 PR2).
 *
 * A Radix Collapsible row (mirrors ExpandableOrderRow) whose panel is a tall
 * three-tier stack: SKU derivation, Designs, and the variant matrix. The header
 * carries the at-a-glance signal (type code, coverage, status pill); the heavy
 * tiers only mount while expanded (design staging lives at page level so it
 * survives collapse).
 *
 * `papMode` renders the stock-product work row instead: it omits POD derivation
 * and print-design controls, while exposing import status, catalogue-wide SKU
 * clashes, and the guarded admin actions used to resolve/add PAP products.
 */
import { memo, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as Collapsible from '@radix-ui/react-collapsible';
import { AlertTriangle, ChevronRight, Info, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { isCustomSyncProduct, type DerivedDesignSet } from '@threadheads/shared';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NoteLine } from '@/components/shared/NoteLine';
import { SkuStatusBadge } from '@/features/inventory/components/SkuStatusBadge';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { StatusPill, type SupplyPillState } from './StatusPill';
import { CoverageBar } from './CoverageBar';
import { SkuDerivationTier } from './SkuDerivationTier';
import { OwnBlankGroupsPanel } from './OwnBlankGroupsPanel';
import { MarkAsPapButton } from './MarkAsPapButton';
import { ProductKindSwitch } from './ProductKindSwitch';
import { PrintTechnologyChip, PrintTechnologyPicker } from './PrintTechnologyPicker';
import { DesignsTier } from './DesignsTier';
import { ByoDesignSetsPanel } from './ByoDesignSetsPanel';
import { productRowDomId } from './MissingFilesDialog';
import { VariantMatrix } from './VariantMatrix';
import { HeldBackNote } from './HeldBackNote';
import { SharedStockTier } from './shared-stock/SharedStockTier';
import {
  SupplyComposer,
  SupplyOwnersError,
  SupplySourceSwitch,
  type SupplyOwner,
  type SupplySource,
} from './supply/SupplyComposer';
import {
  type ScannedSupply,
  SupplyRowChip,
  SupplySavedActions,
  SupplySavedSummary,
  useSupplyElection,
} from './supply/SupplySavedSummary';
import { isSupplyElectionLocked } from '../hooks/useSupplyComposer';
import { ColourDot } from './ColourDot';
import { distinctColours, distinctSizes } from '../lib/status';
import type { CodeTable, PapSkuResolution, PreviewProduct } from '../lib/types';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';
import { effectiveByoSets, type ProductStaging, type StagedDesignsApi } from '../hooks/useStagedDesigns';
import { getPapAddBlocker, papResolvedSku, papSkuEditsBlank } from '../lib/pap-add-eligibility';
import { declaredReasonKey, type StoreSkuSummary } from '../lib/store-skus';
import { coverageCopy, type BlankStep } from '../lib/coverage-copy';
import type { ArtworkUploadTarget } from '../hooks/useArtworkUploadLocation';

export interface ProductSyncRowProps {
  product: PreviewProduct;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  buffer: ProductSyncSettingsBuffer;
  staging: StagedDesignsApi;
  hasCustomPrintSkuMapping: boolean;
  onMapIt: (table: CodeTable, name?: string) => void;
  admin: boolean;
  /** Projection lookup failed this request — `syncedVariantCount` is unknown, so the Synced chip is suppressed rather than shown as 0/N. */
  syncStatusUnavailable: boolean;
  /** BLU-711 Stage 2 dormancy gate: false hides EVERY shared-SKU mapping surface (chip + tier). */
  sharedSkuUiEnabled: boolean;
  /** Link-state load degraded this scan — mapping chips are suppressed, the tier renders an honest note. */
  mappingStatusUnavailable: boolean;
  /** BLU-794: the PAP imported-sku lookup failed, so `syncedVariantCount` is UNKNOWN in pap mode — the Imported chip is suppressed rather than shown as 0/N. */
  importStatusUnavailable?: boolean;
  /** PAP catalogue view: render the stock-product work row (see file header). */
  papMode?: boolean;
  papSkuClash?: boolean;
  papClaimsComplete?: boolean;
  papClaimsError?: string | null;
  papPending?: boolean;
  onRequestPapAdd?: (product: PreviewProduct) => void;
  onLoadPapResolution?: (productId: string) => Promise<PapSkuResolution>;
  onUpdatePapSkus?: (input: { productId: string; changes: Array<{ variantId: string; expectedSku: string; newSku: string }> }) => Promise<{ resolution: PapSkuResolution }>;
  /** Resolved colour-mode upload target + the candidates its picker offers. */
  artworkTarget: ArtworkUploadTarget;
}


function PapWorkRow({
  product,
  summary,
  thumbnail,
  admin,
  importStatusUnavailable,
  papSkuClash,
  papClaimsComplete,
  papClaimsError,
  isSelected,
  onToggleSelect,
  papPending,
  onRequestPapAdd,
  onLoadPapResolution,
  onUpdatePapSkus,
}: {
  product: PreviewProduct;
  summary: string;
  thumbnail: ReactNode;
  admin: boolean;
  importStatusUnavailable: boolean;
  papSkuClash: boolean;
  papClaimsComplete: boolean;
  papClaimsError: string | null;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  papPending: boolean;
  onRequestPapAdd?: (product: PreviewProduct) => void;
  onLoadPapResolution?: (productId: string) => Promise<PapSkuResolution>;
  onUpdatePapSkus?: ProductSyncRowProps['onUpdatePapSkus'];
}) {
  const { t } = useTranslation('productSync');
  const [resolution, setResolution] = useState<PapSkuResolution | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [loadingResolution, setLoadingResolution] = useState(false);
  const [updatingSkus, setUpdatingSkus] = useState(false);
  const fullyImported = product.variantCount > 0 && product.syncedVariantCount >= product.variantCount;

  const loadResolution = async () => {
    setLoadingResolution(true);
    try {
      if (!onLoadPapResolution) return;
      const next = await onLoadPapResolution(product.id);
      setResolution(next);
      setEdits(Object.fromEntries(next.conflicts.map((conflict) => [conflict.variantId, conflict.suggestedSku])));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('papActions.checkFailed'));
    } finally {
      setLoadingResolution(false);
    }
  };

  const applySkuChanges = async () => {
    if (!resolution) return;
    setUpdatingSkus(true);
    try {
      if (!onUpdatePapSkus) return;
      const result = await onUpdatePapSkus({
        productId: product.id,
        changes: resolution.conflicts.map((conflict) => ({
          variantId: conflict.variantId,
          expectedSku: conflict.currentSku,
          newSku: papResolvedSku(edits[conflict.variantId], conflict.suggestedSku),
        })),
      });
      setResolution(result.resolution);
      setEdits(Object.fromEntries(result.resolution.conflicts.map((conflict) => [conflict.variantId, conflict.suggestedSku])));
      toast.success(result.resolution.canAdd ? t('papActions.skusUpdatedClear') : t('papActions.skusUpdatedRecheck'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('papActions.updateFailed'));
      await loadResolution();
    } finally {
      setUpdatingSkus(false);
    }
  };

  const checksUnavailable = Boolean(papClaimsError) || !papClaimsComplete;
  const addBlocker = getPapAddBlocker(product, {
    importStatusUnavailable,
    clashStatusUnavailable: checksUnavailable,
  });
  const addDisabled = addBlocker !== null;
  const selectionEligible = admin && !addDisabled;

  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-center gap-3 px-3 py-2.5">
        {admin && (
          <Checkbox
            checked={isSelected}
            disabled={!selectionEligible}
            onCheckedChange={() => onToggleSelect(product.id)}
            aria-label={t('row.selectAria', { name: product.title })}
          />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed"
          onClick={() => onToggleSelect(product.id)}
          disabled={!selectionEligible}
          aria-label={t('row.selectAria', { name: product.title })}
        >
          {thumbnail}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-semibold">{product.title}</span>
            <span className="block truncate text-[11.5px] text-muted-foreground">{summary}</span>
          </span>
        </button>
        <span className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:w-56 md:w-72 lg:w-[22rem]">
          {product.shopifyStatus === 'draft' && <SkuStatusBadge status="draft" />}
          {papSkuClash && <span className="rounded-md bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-800 ring-1 ring-red-300/80">{t('papView.clashChip')}</span>}
          {!importStatusUnavailable && (
            <span className={cn('rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase ring-1', fullyImported ? 'bg-emerald-100 text-emerald-800 ring-emerald-300/80' : 'bg-muted text-muted-foreground ring-border')}>
              <span aria-hidden="true">{t('papView.importedChip', { imported: product.syncedVariantCount, total: product.variantCount })}</span>
              <span className="sr-only">
                {fullyImported
                  ? t('papView.importedChipAriaAll', { count: product.variantCount })
                  : t('papView.importedChipAria', { imported: product.syncedVariantCount, count: product.variantCount })}
              </span>
            </span>
          )}
          {/* Busy state keeps the LABEL and swaps only the icon (precedent:
              ProductSyncPage's syncButton). Replacing the label with a bare
              spinner leaves the button with no accessible name (WCAG 4.1.2),
              and under `prefers-reduced-motion` the spinner stops animating —
              so the control would go completely silent. `aria-busy` carries the
              state; `motion-reduce:animate-none` is required on this surface. */}
          {admin && papSkuClash && (
            <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => void loadResolution()} disabled={loadingResolution} aria-busy={loadingResolution}>
              {loadingResolution && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {loadingResolution ? t('papActions.resolving') : t('papActions.resolve')}
            </Button>
          )}
          {admin && !fullyImported && (
            <Button size="sm" className="h-7 gap-1 bg-threadheads-purple px-2 text-xs text-white hover:bg-threadheads-purple/90" onClick={() => onRequestPapAdd?.(product)} disabled={addDisabled || papPending} aria-busy={papPending} aria-describedby={addDisabled ? `pap-add-reason-${product.id}` : undefined}>
              {papPending && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {papPending ? t('papActions.adding') : t('papActions.addToErp')}
            </Button>
          )}
        </span>
      </div>
      {admin && !fullyImported && addDisabled && (
        <p id={`pap-add-reason-${product.id}`} className="px-3 pb-2 text-right text-[10px] text-muted-foreground">
          {addBlocker === 'sku_clash' ? t('papActions.resolveFirst') : addBlocker === 'clash_status_unavailable' ? t('papActions.clashStatusUnavailable') : addBlocker === 'import_status_unavailable' ? t('papActions.importStatusUnavailable') : addBlocker === 'variants_truncated' ? t('papActions.tooManyVariants') : addBlocker === 'no_importable_skus' ? t('papActions.noImportableSkus') : t('papActions.addFailed')}
        </p>
      )}

      {resolution && resolution.conflicts.length > 0 && (
        <div className="space-y-3 border-t bg-red-50/40 px-4 py-3">
          <div className="flex items-start gap-2 text-xs text-red-900"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{t('papActions.resolveHint')}</span></div>
          {resolution.conflicts.map((conflict) => (
            <div key={conflict.variantId} className="grid gap-2 rounded-md border bg-background p-3 sm:grid-cols-[1fr_12rem]">
              <div className="min-w-0">
                <p className="text-xs font-semibold">{conflict.variantTitle}</p>
                <p className="truncate text-[11px] text-muted-foreground">{t('papActions.claimedSku', { sku: conflict.currentSku, owners: conflict.owners.map((owner) => owner.productTitle ?? t('papActions.erpOwner')).join(', ') })}</p>
              </div>
              <Input value={edits[conflict.variantId] ?? conflict.suggestedSku} aria-invalid={papSkuEditsBlank(edits, resolution?.conflicts ?? []) && !(edits[conflict.variantId] ?? conflict.suggestedSku).trim() ? true : undefined} onChange={(event) => setEdits((current) => ({ ...current, [conflict.variantId]: event.target.value }))} aria-label={t('papActions.replacementAria', { variant: conflict.variantTitle })} className="h-8 font-mono text-xs" />
            </div>
          ))}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setResolution(null)}>{t('papActions.cancel')}</Button>
            <Button size="sm" onClick={() => void applySkuChanges()} disabled={updatingSkus || papSkuEditsBlank(edits, resolution?.conflicts ?? [])} className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90">
              {updatingSkus && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t('papActions.updateSkus', { count: resolution.conflicts.length })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function TierCard({ n, title, hint, right, children }: { n: ReactNode; title: string; hint?: string; right?: ReactNode; children?: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-lg border bg-background">
      <header className="flex items-center gap-2.5 border-b px-4 py-2.5">
        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-threadheads-purple/10 text-[11px] font-bold text-threadheads-purple">
          {n}
        </span>
        <h3 className="text-[13px] font-semibold">{title}</h3>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
      </header>
      {children && <div className="p-4">{children}</div>}
    </section>
  );
}

/**
 * Tier 1 for an admin's product with a SAVED 3PL blank choice (92-27b,
 * UI-SPEC 1.3 and 1.4). Mounted only when the scan summary carries `supply`,
 * so it alone makes the one `getSupplyElection` read (30 s stale); the owner
 * list is the parent's query. Saved: the summary plus "Change blank" (the
 * composer opens with the saved values; Discard comes back here, a save
 * closes it) and "Stop using this blank". Synced: the synced summary, and
 * (BLU-1522 S2b) "Change blank" still opens the composer with the synced
 * rows locked, so a new colour or the variants not synced yet can change;
 * "Stop using this blank" is not offered (the clear refuses it).
 */
function SavedSupplyTier1({
  product,
  summary,
  owners,
  admin,
  staged,
  customProduct,
  onPillStateChange,
  onCleared,
}: {
  product: PreviewProduct;
  summary: ScannedSupply;
  owners: SupplyOwner[];
  admin: boolean;
  staged: ProductStaging;
  /** The row's custom decision: the composer and the summary hide light or dark. */
  customProduct: boolean;
  onPillStateChange: (state: SupplyPillState | null) => void;
  onCleared: () => void;
}) {
  const { t } = useTranslation('productSync');
  const [changing, setChanging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const electionQuery = useSupplyElection(product.id, true);
  const election = electionQuery.data?.election ?? null;
  const summaryOwner = owners.find((o) => o.id === summary.ownerOrganizationId) ?? null;
  const ownerName = summaryOwner?.name ?? election?.ownerName ?? null;
  const locked = election ? isSupplyElectionLocked(election) : summary.fullySynced;
  const title = ownerName ? t('supply.tier1.title', { owner: ownerName }) : t('supply.saved.titleUnknown');

  if (changing && summaryOwner && election) {
    return (
      <TierCard n="1" title={title} hint={t('supply.tier1.hint')}>
        <SupplyComposer
          key={summaryOwner.id}
          product={product}
          owner={summaryOwner}
          admin={admin}
          staged={staged}
          initialElection={election}
          customProduct={customProduct}
          onDiscard={() => setChanging(false)}
          onSaved={() => setChanging(false)}
          onPillStateChange={onPillStateChange}
        />
      </TierCard>
    );
  }

  // Actions wait for the saved choice, so a synced product never flashes "Stop using".
  const actionsReady = admin && !electionQuery.isLoading;
  const canChange = election !== null && summaryOwner?.blanksAllowed === true;
  return (
    <TierCard
      n="1"
      title={title}
      hint={locked ? undefined : t('supply.tier1.hint')}
      right={
        actionsReady ? (
          <SupplySavedActions
            productId={product.id}
            ownerName={ownerName}
            canChange={canChange}
            canStop={!locked}
            onChangeBlank={() => {
              setNotice(null);
              setChanging(true);
            }}
            onCleared={onCleared}
            onAlreadySynced={() => setNotice(t('supply.saved.stopSynced'))}
          />
        ) : undefined
      }
    >
      <SupplySavedSummary
        product={product}
        summary={summary}
        election={election}
        electionLoading={electionQuery.isLoading}
        electionError={electionQuery.isError}
        onRetry={() => void electionQuery.refetch()}
        ownerName={ownerName}
        locked={locked}
        admin={admin}
        canChange={actionsReady && canChange}
        notice={notice}
        customProduct={customProduct}
      />
    </TierCard>
  );
}

/**
 * What step 1 offers about the 3PL's blank (BLU-1522 S6a), for the header's
 * pill and coverage line, which show while the row is closed (so step 1 is not
 * mounted). Mounted only for an admin row whose step 1 is about a blank: a
 * supply-only client's product with none chosen yet (`ownerId` null), or a
 * saved blank that needs attention (`ownerId` its 3PL). It reads the same
 * `listSharedStockOwners` query (same key) as step 1, so it adds no read.
 *
 * Mirrors step 1: while the list loads, fails or is empty, step 1 is not the
 * blank composer (null); a listed 3PL that shares blanks (the saved 3PL, or
 * any) means a blank can be chosen now (`picker`); otherwise `noBlanks`.
 */
function BlankStepProbe({ ownerId, onChange }: { ownerId: string | null; onChange: (step: BlankStep) => void }) {
  const ownersQuery = trpc.inventory.listSharedStockOwners.useQuery(undefined, { staleTime: 300_000 });
  const owners = ownersQuery.isError ? [] : (ownersQuery.data?.owners ?? []);
  const step: BlankStep =
    owners.length === 0
      ? null
      : owners.some((o) => o.blanksAllowed === true && (ownerId === null || o.id === ownerId))
        ? 'picker'
        : 'noBlanks';
  useEffect(() => onChange(step), [onChange, step]);
  useEffect(() => () => onChange(null), [onChange]);
  return null;
}

/**
 * Tier 1 for an admin of a client with a 3PL connection (Phase 92, UI-SPEC
 * 1.1). Mounted only behind the row's gate (admin, a 3PL connection on this
 * scan, not store SKU mode), so nobody else fires the owner read; for those
 * admins it is the same `listSharedStockOwners` query (same key) Tier 4
 * already makes, so it adds no read.
 *
 * While the list loads, or when it is empty, Tier 1 is today's card (UI 1,
 * UI 28). When it fails, today's card plus one muted line and a retry (UI 29).
 * Otherwise the "Blank" source switch sits above the body, and choosing the
 * 3PL swaps the body for the composer and the title for the supply title.
 *
 * `supplyOnly` (BLU-1522 S6a): the client said in onboarding it prints only
 * on its 3PL's blanks, so "My own blanks" is never offered: the composer is
 * the body from the start, Discard and "Stop using this blank" land back on
 * it, and the switch keeps only the 3PL picker (none with one 3PL).
 */
function SupplyAwareTier1({
  product,
  admin,
  right,
  ownBody,
  staged,
  customProduct,
  supplyOnly,
  onPillStateChange,
}: {
  product: PreviewProduct;
  admin: boolean;
  right: ReactNode;
  ownBody: ReactNode;
  /** The product's staged designs (which print sets carry a back), for the save. */
  staged: ProductStaging;
  /** The row's custom decision, for the saved tier and the composer. */
  customProduct: boolean;
  /** The org prints only on the blanks of its 3PL (blankSource supply): no "My own blanks". */
  supplyOnly: boolean;
  /** The open draft's header pill state (UI-SPEC 1.5). */
  onPillStateChange: (state: SupplyPillState | null) => void;
}) {
  const { t } = useTranslation('productSync');
  const ownersQuery = trpc.inventory.listSharedStockOwners.useQuery(undefined, { staleTime: 300_000 });
  const owners = ownersQuery.data?.owners ?? [];
  const [source, setSource] = useState<SupplySource>('own');
  const [ownerChoice, setOwnerChoice] = useState<string | null>(null);
  // 92-27b (UI-SPEC 1.3, 1.4): a saved choice from the scan summary. `cleared`
  // hides it at once after "Stop using this blank" succeeds, before the scan
  // page refetches; a later save shows it again.
  const [cleared, setCleared] = useState(false);
  const summary = !cleared ? (product.supply ?? null) : null;

  if (summary) {
    return (
      <SavedSupplyTier1
        product={product}
        summary={summary}
        owners={owners}
        admin={admin}
        staged={staged}
        customProduct={customProduct}
        onPillStateChange={onPillStateChange}
        onCleared={() => {
          setCleared(true);
          setSource('own');
        }}
      />
    );
  }

  if (ownersQuery.isError) {
    return (
      <TierCard n="1" title={t('tier1.title')} hint={t('tier1.hint')} right={right}>
        <div className="flex flex-col gap-4">
          <SupplyOwnersError onRetry={() => void ownersQuery.refetch()} retrying={ownersQuery.isFetching} />
          {ownBody}
        </div>
      </TierCard>
    );
  }

  if (owners.length === 0) {
    return (
      <TierCard n="1" title={t('tier1.title')} hint={t('tier1.hint')} right={right}>
        {ownBody}
      </TierCard>
    );
  }

  const owner = owners.find((o) => o.id === ownerChoice) ?? (owners.length === 1 ? owners[0]! : null);
  const supplyOn = supplyOnly || source === 'supply';

  return (
    <TierCard
      n="1"
      title={
        supplyOn && owner
          ? t('supply.tier1.title', { owner: owner.name })
          : supplyOnly
            ? t('supply.saved.titleUnknown')
            : t('tier1.title')
      }
      hint={supplyOn && (owner || supplyOnly) ? t('supply.tier1.hint') : t('tier1.hint')}
      right={right}
    >
      <div className="flex flex-col gap-4">
        <SupplySourceSwitch
          productId={product.id}
          owners={owners}
          source={supplyOn ? 'supply' : 'own'}
          ownerId={owner?.id ?? null}
          onSourceChange={setSource}
          onOwnerChange={setOwnerChoice}
          admin={admin}
          // BLU-1522 S8: a product on the org's own base garments per colour
          // or size uses one source for the whole product (the plan holds a
          // mix back as `base_garment_sources_mixed`).
          supplyDisabledReason={product.ownBlankGroups ? t('ownBlanks.supplyOff') : undefined}
          supplyOnly={supplyOnly}
        />
        {/* Switching back to "My own blanks" unmounts the composer, which
            discards the draft with no confirm: nothing was written. */}
        {!supplyOn ? (
          ownBody
        ) : owner ? (
          <SupplyComposer
            key={owner.id}
            product={product}
            owner={owner}
            admin={admin}
            staged={staged}
            customProduct={customProduct}
            onDiscard={() => setSource('own')}
            onSaved={() => setCleared(false)}
            onPillStateChange={onPillStateChange}
          />
        ) : null}
      </div>
    </TierCard>
  );
}

/**
 * Tier 1 in store SKU mode (Phase 88, S16, S17): what the mode does, plus why
 * a partial product can't be fully set up or that a set-up product's SKU
 * changed. Nothing here is minted, derived or written to the store, so the
 * derivation tier's preview, its "Read from Shopify" hint and its metafield
 * mapping note have no place here. The SKUs themselves are in step 3, behind
 * its "Show SKUs" switch (BLU-1522 S6a: a list of every SKU here was noise).
 */
function StoreSkuTierBody({ summary }: { summary: StoreSkuSummary }) {
  const { t } = useTranslation('productSync');
  const reasonKey = summary.status === 'partial' ? declaredReasonKey(summary.blockingReason) : null;
  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs text-muted-foreground">{t('tier1.storeSkuBody')}</p>
      {reasonKey && (
        <HeldBackNote>
          {t(summary.setUp === 0 ? 'tier1.storeSkuPartialNoneBody' : 'tier1.storeSkuPartialBody', { reason: t(reasonKey) })}
        </HeldBackNote>
      )}
      {summary.status === 'changed' && <HeldBackNote>{t('tier1.storeSkuChangedBody')}</HeldBackNote>}
    </div>
  );
}

export const ProductSyncRow = memo(function ProductSyncRow({
  product,
  isExpanded,
  onToggleExpand,
  isSelected,
  onToggleSelect,
  buffer,
  staging,
  hasCustomPrintSkuMapping,
  onMapIt,
  admin,
  syncStatusUnavailable,
  sharedSkuUiEnabled,
  mappingStatusUnavailable,
  importStatusUnavailable = false,
  papMode = false,
  papSkuClash = false,
  papClaimsComplete = false,
  papClaimsError = null,
  papPending = false,
  onRequestPapAdd,
  onLoadPapResolution,
  onUpdatePapSkus,
  artworkTarget,
}: ProductSyncRowProps) {
  const { t } = useTranslation('productSync');

  const colours = distinctColours(product);
  const sizes = distinctSizes(product);
  // Show the variant matrix whenever there are variants — a colour-less product
  // renders a single "No colour" row, not an omitted tier.
  const hasVariants = product.variants.length > 0;

  // Phase 88, store SKU mode (the server sent a `storeSku` summary): the
  // collapsed header shows the store SKU pill alone. The derived coverage and
  // the Synced chip count minted SKUs, which this mode never writes, so next
  // to the pill they would contradict it. Tier 1 shows the store SKU body and
  // tier 3 the store SKU column. Without a summary, unchanged.
  const storeSku = product.storeSku;
  const storeSkuMode = storeSku != null;
  // The shared custom rule over the SAVED settings: what the run plans from
  // and what the Mixed switch shows, so they flip together. Store SKU mode
  // keeps today's tiers.
  const customProduct = !storeSkuMode && isCustomSyncProduct(buffer.serverSettings, product.productType.name ?? '', product.id);
  // Phase 92 (UI-SPEC 1.1): the supply source switch is for admins of a
  // client with a 3PL connection (`sharedSkuUiEnabled` is that head-count),
  // never in store SKU mode. Everyone else gets today's Tier 1 and no read.
  const supplyEligible = admin && sharedSkuUiEnabled && !storeSkuMode;
  // Phase 92 (92-27b, UI-SPEC 1.3 to 1.5): a product whose scan summary says
  // it is built on a 3PL's blank (saved or synced). From the page's existing
  // scan read; never in store SKU mode. Everyone else: undefined, no change.
  const supply = !storeSkuMode ? (product.supply ?? null) : null;
  // Phase 86 slice B (86-28): a usable BYO product's design ID and metafield
  // letters go into staging from the row itself (always mounted, open or
  // not), so the page's readiness and run never depend on the panel having
  // been opened. A product the reader cannot use drops its entry.
  // 86-23: a store SKU row never uses BYO design IDs (they are for new
  // products, which come as derived rows), so it renders as it always has.
  // A custom product never uses BYO (no design ID of its own; the server
  // sends it no BYO answer), so it never stages one either.
  const byoOk = !storeSkuMode && !customProduct && product.byoDesign?.ok === true;
  const metafieldSets = useMemo(() => {
    const out: Record<string, DerivedDesignSet> = {};
    for (const v of product.variants) if (v.byoSet) out[v.id] = v.byoSet;
    return out;
  }, [product.variants]);
  const { setByoBase } = staging;
  const productDesignCode = product.designCode;
  useEffect(() => {
    setByoBase(product.id, byoOk ? { designCode: productDesignCode, metafieldSets } : null);
  }, [setByoBase, product.id, byoOk, productDesignCode, metafieldSets]);
  // Every BYO row follows each variant's chosen set (the operator's choice,
  // else the metafield's): the designs (a Medium variant gets medium slots),
  // the SKU shape in step 1 and the patterns in step 3, and the light / dark
  // / medium panel with the warning's one-click fixes. A store SKU row is
  // never a BYO row (86-23 split the page). Every other row is unchanged.
  const byoPanel = byoOk;
  const byoChoice = byoOk ? staging.getByo(product.id) : null;
  const byoVariantSets = useMemo(
    () => (byoOk ? (byoChoice ? effectiveByoSets(byoChoice) : metafieldSets) : undefined),
    [byoOk, byoChoice, metafieldSets],
  );
  // The open draft's pill state, reported by the composer while it is mounted.
  const [draftPill, setDraftPill] = useState<SupplyPillState | null>(null);
  // BLU-1522 S6a: a client that prints only on the blanks of its 3PL (the
  // onboarding answer blankSource supply): step 1 drops "My own blanks".
  const supplyOnly = buffer.serverSettings.blankSource === 'supply';
  // What step 1 offers about the blank, so the header never asks for a choice
  // step 1 does not offer (a non-admin, or a 3PL list that failed, sees the
  // own-blank view). Reported by BlankStepProbe; null when it is not mounted.
  const blankProbe = supplyEligible && (supply ? supply.attention : supplyOnly);
  const [probedBlankStep, setProbedBlankStep] = useState<BlankStep>(null);
  const blankStep = blankProbe ? probedBlankStep : null;
  // A saved choice that can be built reads "Ready"; one whose blank needs
  // attention asks for a blank again, as does a supply-only product with no
  // blank chosen yet, when step 1 offers the picker. An open draft speaks for
  // itself.
  const supplyPill: SupplyPillState | null =
    draftPill ??
    (supply
      ? supply.attention
        ? { kind: 'choose_blank' }
        : { kind: 'ready' }
      : blankStep === 'picker'
        ? { kind: 'choose_blank' }
        : null);
  // The header's coverage line in plain words (BLU-1522 S6a), counted the way
  // the product is built: on a 3PL's blank, or from the org's code tables.
  const coverage = coverageCopy(product, supply, blankStep);
  const coverageLine = t(coverage.key, coverage.values);

  // Apply-engine progress ("Synced M/N") sits with the STATUS pill, never in the
  // coverage band: two adjacent M/N ratios (derived vs synced) read as one
  // number and get conflated. Hidden entirely when the count is unknown.
  const showSyncedChip = !storeSkuMode && !syncStatusUnavailable && product.syncedVariantCount > 0;
  const fullySynced = product.variantCount > 0 && product.syncedVariantCount >= product.variantCount;

  // Shared-SKU mapping chip (BLU-711 Stage 2). Same pill geometry as the
  // Synced chip; neutral outline like the Stage-1 /products "Shared" badge
  // ("shared" is a state, not a promotion) — EXCEPT attention, which borrows
  // the amber attention discipline. Suppressed entirely when the dimension is
  // dormant or degraded (honest degradation, mirrors the Synced chip).
  const mappingAttention = product.mappingAttentionReasons.length > 0;
  const showMappingChip =
    !supply &&
    sharedSkuUiEnabled &&
    !mappingStatusUnavailable &&
    (mappingAttention || product.mappedVariantCount > 0 || product.electedVariantCount > 0);
  const mappingAttentionLabels = mappingAttention
    ? product.mappingAttentionReasons
        .map((reason) => t(`sharedStock.reason.${reason}`, { defaultValue: reason }))
        .join(', ')
    : '';

  // Summary line: "N variants · C colours × S sizes" (glyphs, not em dashes).
  const summaryParts = [t('row.variants', { count: product.variantCount })];
  if (colours.length > 0) summaryParts.push(t('row.colours', { count: colours.length }));
  const summary = sizes.length > 0
    ? `${summaryParts.join(' · ')} × ${t('row.sizes', { count: sizes.length })}`
    : `${summaryParts.join(' · ')} · ${t('row.singleSize')}`;

  const thumbnail = (
    <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md border bg-muted">
      {product.imageUrl ? (
        <img src={product.imageUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <ColourDot name={null} className="h-4 w-4" />
      )}
    </span>
  );

  if (papMode) {
    return <PapWorkRow product={product} summary={summary} thumbnail={thumbnail} admin={admin} importStatusUnavailable={importStatusUnavailable} papSkuClash={papSkuClash} papClaimsComplete={papClaimsComplete} papClaimsError={papClaimsError} isSelected={isSelected} onToggleSelect={onToggleSelect} papPending={papPending} onRequestPapAdd={onRequestPapAdd} onLoadPapResolution={onLoadPapResolution} onUpdatePapSkus={onUpdatePapSkus} />;
  }

  return (
    <Collapsible.Root
      id={productRowDomId(product.id)}
      open={isExpanded}
      onOpenChange={() => onToggleExpand(product.id)}
      className="border-b last:border-b-0"
    >
      {blankProbe && <BlankStepProbe ownerId={supply?.ownerOrganizationId ?? null} onChange={setProbedBlankStep} />}
      <div
        className={cn(
          'flex items-center gap-3 px-3 py-2.5',
          isExpanded ? 'bg-threadheads-purple/[0.04]' : 'hover:bg-muted/40 motion-reduce:hover:bg-muted/40',
        )}
      >
        {/* Selection */}
        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(product.id)}
            aria-label={t('row.selectAria', { name: product.title })}
          />
        </div>

        {/* Trigger wraps everything else so the whole row toggles */}
        <Collapsible.Trigger asChild>
          <button
            type="button"
            aria-label={t('row.expandAria', { name: product.title })}
            className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            <ChevronRight className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none', isExpanded && 'rotate-90 text-threadheads-purple')} />

            {/* Thumbnail */}
            {thumbnail}

            {/* Product identity */}
            <span className="min-w-0 flex-1">
              {/* Phase 96: the DTG / DTF pick rides the title line, where the
                  title truncates to make room; the status cluster's fixed
                  widths already hold three nowrap pills. The Draft badge
                  (the list takes drafts) rides it for the same reason. */}
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[13.5px] font-semibold">{product.title}</span>
                {product.shopifyStatus === 'draft' && <SkuStatusBadge status="draft" />}
                <PrintTechnologyChip product={product} />
              </span>
              <span className="block truncate text-[11.5px] text-muted-foreground">{summary}</span>
            </span>

            {/* Type chip */}
            <span className="hidden w-36 shrink-0 items-center gap-1.5 text-[11.5px] text-muted-foreground sm:flex">
              <span className="truncate">{product.productType.name ?? '-'}</span>
              {/* A product on a 3PL's blank shows the blank's own type code (UI-SPEC 1.5). */}
              {supply ? (
                <span
                  className="shrink-0 rounded bg-threadheads-purple/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-threadheads-purple"
                  title={(supply.garmentKeys?.length ?? 0) > 1 ? supply.garmentKeys.join(', ') : undefined}
                >
                  {supply.garmentKey}
                  {(supply.garmentKeys?.length ?? 0) > 1 && ` +${supply.garmentKeys.length - 1}`}
                </span>
              ) : product.productType.code ? (
                <span className="shrink-0 rounded bg-threadheads-purple/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-threadheads-purple">
                  {product.productType.code}
                </span>
              ) : product.productType.name ? (
                <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-300/80">
                  {t('type.unmapped')}
                </span>
              ) : null}
            </span>

            {/* Coverage (print SKUs ready; not shown in store SKU mode).
                Nothing ready: the empty bar gives way to the next step. */}
            {!storeSkuMode && (
              <span className="hidden w-40 shrink-0 flex-col gap-1 md:flex">
                <span className="text-[11px] tabular-nums text-muted-foreground">{coverageLine}</span>
                {coverage.actionKey ? (
                  <span className="text-[11px] font-medium leading-tight text-amber-700">{t(coverage.actionKey)}</span>
                ) : (
                  <CoverageBar value={coverage.pct} complete={coverage.complete} label={coverageLine} />
                )}
              </span>
            )}

            {/* Status (+ apply-engine "Synced M/N" + shared-SKU mapping chip).
                PR #1667 review: three nowrap pills need lg:w-72; the mapping
                chip defers to lg so md keeps the original two-pill fit. */}
            <span className="flex shrink-0 items-center justify-end gap-1.5 sm:w-32 md:w-48 lg:w-72">
              {supply && <SupplyRowChip summary={supply} admin={admin} />}
              {showMappingChip && (
                <span
                  className={cn(
                    'hidden shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 lg:inline-flex',
                    mappingAttention
                      ? 'bg-amber-100 text-amber-800 ring-amber-300/80'
                      : 'bg-background text-muted-foreground ring-border',
                  )}
                >
                  <Link2 className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                  <span aria-hidden="true">
                    {mappingAttention
                      ? t('mappingFilter.attentionChip')
                      : t('mappingFilter.chipLabel', {
                          mapped: product.mappedVariantCount,
                          count: product.variantCount,
                        })}
                  </span>
                  <span className="sr-only">
                    {mappingAttention
                      ? t('mappingFilter.attentionChipAria', { reasons: mappingAttentionLabels })
                      : t('mappingFilter.chipAria', {
                          mapped: product.mappedVariantCount,
                          count: product.variantCount,
                        })}
                  </span>
                </span>
              )}
              {showSyncedChip && (
                <span
                  className={cn(
                    // Same pill geometry as StatusPill (ring, not border) so the
                    // pair reads as one status cluster.
                    'hidden shrink-0 whitespace-nowrap rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 md:inline-flex',
                    fullySynced
                      ? 'bg-emerald-100 text-emerald-800 ring-emerald-300/80'
                      : 'bg-muted text-muted-foreground ring-border',
                  )}
                >
                  <span aria-hidden="true">
                    {t('syncFilter.chipLabel', { synced: product.syncedVariantCount, count: product.variantCount })}
                  </span>
                  <span className="sr-only">
                    {t('syncFilter.chipAria', { synced: product.syncedVariantCount, count: product.variantCount })}
                  </span>
                </span>
              )}
              <StatusPill product={product} supply={supplyPill} />
            </span>
          </button>
        </Collapsible.Trigger>
      </div>

      <Collapsible.Content>
        <div className="flex flex-col gap-4 border-t bg-muted/30 p-4">
          {/* BLU-1373: a product of a Mixed type is set Custom or Ready-made
              here (renders nothing otherwise). Never in store SKU mode. */}
          {!storeSkuMode && (
            <ProductKindSwitch
              product={product}
              buffer={buffer}
              admin={admin}
              syncStatusUnavailable={syncStatusUnavailable}
            />
          )}
          {/* Phase 96: Default / DTG / DTF for this product, store SKU mode
              too. Renders nothing on a 3PL's blank (product.supply). */}
          <PrintTechnologyPicker product={product} />
          {/* Phase 88: the server's store SKU summary switches tier 1 to the
              store SKU body. Without it, today's derivation tier, unchanged. */}
          {supplyEligible ? (
            <SupplyAwareTier1
              product={product}
              admin={admin}
              right={<MarkAsPapButton product={product} />}
              staged={staging.get(product.id)}
              customProduct={customProduct}
              supplyOnly={supplyOnly}
              onPillStateChange={setDraftPill}
              ownBody={
                <div className="flex flex-col gap-4">
                  <SkuDerivationTier
                    product={product}
                    buffer={buffer}
                    hasCustomPrintSkuMapping={hasCustomPrintSkuMapping}
                    onMapIt={onMapIt}
                    customProduct={customProduct}
                    byoVariantSets={byoVariantSets}
                  />
                  {/* BLU-1522 S8: own base garments per colour or size (admins; renders nothing otherwise). */}
                  <OwnBlankGroupsPanel product={product} buffer={buffer} admin={admin} onMapIt={onMapIt} />
                </div>
              }
            />
          ) : supply ? (
            // A non-admin (Coordinator) reads the saved or synced choice with
            // no actions and no added read: the scan summary alone.
            <TierCard
              n="1"
              title={t('supply.saved.titleUnknown')}
              hint={supply.fullySynced ? undefined : t('supply.tier1.hint')}
            >
              <SupplySavedSummary
                product={product}
                summary={supply}
                election={null}
                electionLoading={false}
                electionError={false}
                ownerName={null}
                locked={supply.fullySynced}
                admin={admin}
                customProduct={customProduct}
              />
            </TierCard>
          ) : (
            <TierCard
              n="1"
              title={storeSkuMode ? t('tier1.storeSkuTitle') : t('tier1.title')}
              hint={storeSkuMode ? t('tier1.storeSkuHint') : t('tier1.hint')}
              right={admin ? <MarkAsPapButton product={product} /> : undefined}
            >
              {storeSkuMode ? (
                <StoreSkuTierBody summary={storeSku} />
              ) : (
                <div className="flex flex-col gap-4">
                  <SkuDerivationTier
                    product={product}
                    buffer={buffer}
                    hasCustomPrintSkuMapping={hasCustomPrintSkuMapping}
                    onMapIt={onMapIt}
                    customProduct={customProduct}
                    byoVariantSets={byoVariantSets}
                  />
                  <OwnBlankGroupsPanel product={product} buffer={buffer} admin={admin} onMapIt={onMapIt} />
                </div>
              )}
            </TierCard>
          )}

          <TierCard n="2" title={t('tier2.title')}>
            {/* Phase 86 slice B: the BYO light / dark / medium choice is the
                step's first block. */}
            {byoPanel && (
              <div className="mb-4 border-b pb-4">
                <ByoDesignSetsPanel product={product} staging={staging} admin={admin} />
              </div>
            )}
            {/* A custom product's artwork comes with each order, so it gets
                one line instead of the design slots. */}
            {customProduct ? (
              <NoteLine icon={Info}>{t('tier2.customBody')}</NoteLine>
            ) : (
              <DesignsTier
                product={product}
                colours={colours}
                buffer={buffer}
                staging={staging}
                admin={admin}
                artworkTarget={artworkTarget}
                // Phase 92: a product on a 3PL's blank groups by the client's
                // own light or dark choice (D-24) and reads the 3PL's platen
                // rules (D-09). Both come from the scan summary; no added read
                // for any other product.
                darkColourNames={supply ? supply.darkColourNames : undefined}
                byoVariantSets={byoVariantSets}
                supplyContext={supply !== null}
              />
            )}
          </TierCard>

          {/* BLU-1425: a product on a 3PL's blank has no Variants step: step 1
              shows the blank with the ready and held-back counts (and lists
              each ready variant once synced). Its truncation note shows here. */}
          {hasVariants && !supply && (
            <TierCard
              n="3"
              title={t('tier3.title')}
              hint={storeSkuMode ? t('tier3.storeSkuHint') : t('tier3.hint')}
            >
              <VariantMatrix
                product={product}
                colourlessCode={buffer.settings.colourlessCode}
                showStoreSku={storeSkuMode}
                byoVariantSets={byoVariantSets}
              />
            </TierCard>
          )}
          {hasVariants && supply && product.variantsTruncated && (
            <HeldBackNote>{t('tier3.truncatedBanner')}</HeldBackNote>
          )}

          {/* Shared-SKU elections live HERE, per blank-code group — never in
              the variant matrix (ONE-CALL-PER-GROUP, see SharedStockTier).
              A product on a 3PL's blank never renders it: its alias holds no
              stock, so back-up has nothing to back (D-05, D-07). */}
          {sharedSkuUiEnabled && hasVariants && !supply && (
            <TierCard n="4" title={t('sharedStock.tierTitle')} hint={t('sharedStock.tierHint')}>
              <SharedStockTier
                product={product}
                admin={admin}
                mappingStatusUnavailable={mappingStatusUnavailable}
              />
            </TierCard>
          )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
});
